import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * Shared furniture for both sheet pages.
 *
 * Sizes are in mm throughout, so what the admin previews on screen is the same
 * geometry that lands on paper. A4 is 210×297mm; the page padding leaves 174mm
 * of content width.
 */

interface SheetPageProps {
  /** Right-hand masthead label: what this side of the sheet is for. */
  kind: string;
  children: ReactNode;
}

/** One A4 side, with the orange bleed band and the brand masthead. */
export function SheetPage({ kind, children }: SheetPageProps) {
  return (
    <section
      className={cn(
        "kit-sheet-page bg-background relative flex flex-col overflow-hidden",
        "h-[297mm] w-[210mm] shrink-0 px-[18mm] pt-[20mm] pb-[14mm]",
        "shadow-elevation break-after-page last:break-after-auto",
      )}
    >
      {/* The one accent on the page, bled to the paper edge. `daylight` is
          brand Yellow, the CTA colour the flow uses; `accent` is brand Black
          and would read as a mourning band across the top of the sheet. */}
      <div className="bg-daylight absolute inset-x-0 top-0 h-[4mm]" />

      <header className="border-primary flex items-end justify-between border-b-[0.4mm] pb-[3.5mm]">
        {/*
         * Horizontal lockup in the top-left corner: logo, hairline, product.
         *
         * The official red-box wordmark, used as supplied and never recoloured
         * or redrawn — it is a trademark, and the sheet is the one surface a
         * homeowner will hold. The hairline keeps "SmartClimate" from reading as
         * part of the mark itself.
         *
         * Height-driven so the mark's own 2.89:1 ratio decides the width (about
         * 22mm at 7.5mm tall). `kit-sheet-shot` enrols it in the route's
         * before-print asset gate: the masthead must not print as a gap.
         */}
        <span className="flex items-center gap-[3.5mm]">
          <img
            src="/brand/velux-logo.svg"
            alt="VELUX"
            className="kit-sheet-shot h-[7.5mm] w-auto shrink-0"
          />
          <span className="bg-border-strong h-[6mm] w-[0.25mm] shrink-0" />
          <span className="text-primary text-[13pt] leading-none font-bold tracking-[-0.1pt]">
            SmartClimate
          </span>
        </span>
        <span className="text-muted text-[8.5pt] tracking-[0.9pt] uppercase">{kind}</span>
      </header>

      {children}
    </section>
  );
}

/** Wide-tracked uppercase label, the sheet's quietest text role. */
export function SheetLabel({ children }: { children: ReactNode }) {
  return <span className="text-muted text-[8pt] tracking-[0.8pt] uppercase">{children}</span>;
}

/** Pushes whatever follows to the foot of the page. */
export function SheetSpacer() {
  return <div className="flex-1" />;
}
